/**
 * `@zion/sdk-surfaces/importmap`: the one parser of the shell's import-map manifest, the emitter
 * of the page's one import map, and the runtime's own URL (layered shell surfaces, D23, R-2, R-5,
 * R-27). The loader bundles this entry and runs it before any import map exists, so it imports
 * nothing.
 *
 * The manifest is `/importmap/manifest.json`, whose contract is
 * `cloud/platform-sdk/schemas/importmap-manifest.schema.json`. Every rule below is that schema's.
 */
/** The runtime's own bare specifier, the one key every manifest carries (R-2). */
export const SURFACES_RUNTIME_SPECIFIER = '@zion/sdk-surfaces/v1';
/** The most entries one map holds (`maxProperties`). */
export const IMPORT_MAP_MAX_ENTRIES = 64;
/** The longest specifier (`propertyNames.maxLength`, npm's package-name cap). */
export const IMPORT_MAP_SPECIFIER_MAX_LENGTH = 214;
/** The longest chunk URL (`$defs.chunkUrl.maxLength`). */
export const IMPORT_MAP_URL_MAX_LENGTH = 512;
/** A bare specifier: lower case, an optional `@scope/`, optional `/subpath` segments. Linear: each class excludes the next literal. */
const SPECIFIER_PATTERN = /^(@[a-z0-9][a-z0-9._-]*\/)?[a-z0-9][a-z0-9._-]*(\/[a-z0-9][a-z0-9._-]*)*$/;
/** A same-origin chunk under `/importmap/`, ending `.js`. No segment starts with a dot, so no `..` segment exists. */
const CHUNK_URL_PATTERN = /^\/importmap\/[A-Za-z0-9_-][A-Za-z0-9._-]*(\/[A-Za-z0-9_-][A-Za-z0-9._-]*)*\.js$/;
/**
 * Thrown by every function of this entry. A message may name a specifier; it never repeats a URL
 * value.
 */
export class ImportMapError extends Error {
    name = 'ImportMapError';
    /** The closed reason. */
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/**
 * Parse the manifest JSON (already `JSON.parse`d) into an {@link ImportMap}.
 *
 * Reads the `imports` member and ignores every other member except `scopes` (WS-2 Q-6). Throws
 * {@link ImportMapError}, checked in this order:
 * - `manifest-invalid`: not a plain object;
 * - `scopes-refused`: a `scopes` member of any value (D23: one map, no scopes);
 * - `manifest-invalid`: `imports` absent or not a plain object, zero entries or more than
 *   {@link IMPORT_MAP_MAX_ENTRIES};
 * - `bad-specifier`: a key over {@link IMPORT_MAP_SPECIFIER_MAX_LENGTH} characters or outside the
 *   bare-specifier grammar (relative, absolute, a scheme, upper case);
 * - `bad-url`: a value that is not a string, is over {@link IMPORT_MAP_URL_MAX_LENGTH} characters,
 *   or is not `/importmap/<path>.js` with no dot-led segment;
 * - `runtime-missing`: no {@link SURFACES_RUNTIME_SPECIFIER} entry.
 *
 * Returns a frozen copy; the input is not mutated. Pure.
 */
export function parseImportMapManifest(json) {
    if (!isPlainObject(json))
        throw new ImportMapError('manifest-invalid', 'the import-map manifest is not a JSON object');
    if (Object.hasOwn(json, 'scopes'))
        throw new ImportMapError('scopes-refused', 'the import-map manifest carries scopes; one map, no scopes');
    const imports = json['imports'];
    if (!Object.hasOwn(json, 'imports') || !isPlainObject(imports)) {
        throw new ImportMapError('manifest-invalid', 'the import-map manifest has no imports object');
    }
    const keys = Object.keys(imports);
    if (keys.length === 0 || keys.length > IMPORT_MAP_MAX_ENTRIES) {
        throw new ImportMapError('manifest-invalid', `the import map holds ${keys.length} entries; 1 to ${IMPORT_MAP_MAX_ENTRIES} are allowed`);
    }
    const out = {};
    for (const key of keys) {
        if (key.length > IMPORT_MAP_SPECIFIER_MAX_LENGTH || !SPECIFIER_PATTERN.test(key)) {
            throw new ImportMapError('bad-specifier', `'${key.slice(0, IMPORT_MAP_SPECIFIER_MAX_LENGTH)}' is not a bare specifier`);
        }
        const url = imports[key];
        if (typeof url !== 'string' || url.length > IMPORT_MAP_URL_MAX_LENGTH || !CHUNK_URL_PATTERN.test(url)) {
            throw new ImportMapError('bad-url', `the chunk URL for '${key}' is not a /importmap/ path ending .js`);
        }
        Object.defineProperty(out, key, { value: url, enumerable: true });
    }
    if (!Object.hasOwn(out, SURFACES_RUNTIME_SPECIFIER)) {
        throw new ImportMapError('runtime-missing', `the import map has no '${SURFACES_RUNTIME_SPECIFIER}' entry`);
    }
    return Object.freeze({ imports: Object.freeze(out) });
}
/**
 * Write `map` into `doc` as the page's one `<script type="importmap">`, appended to `doc.head`.
 *
 * Re-validates `map` through {@link parseImportMapManifest} first, so only a validated map is
 * emitted, and throws that function's errors. Throws `map-present` when `doc` already holds a
 * `script[type="importmap"]`, because a browser honours one map per document. The script's text is
 * `JSON.stringify({ imports })`, set through `textContent` (00-index.md §3). Returns the element.
 */
export function emitImportMap(map, doc = document) {
    const validated = parseImportMapManifest(map);
    if (doc.querySelector('script[type="importmap"]') !== null) {
        throw new ImportMapError('map-present', 'the document already holds an import map');
    }
    const script = doc.createElement('script');
    script.type = 'importmap';
    script.textContent = JSON.stringify({ imports: validated.imports });
    doc.head.append(script);
    return script;
}
/**
 * The URL the map serves {@link SURFACES_RUNTIME_SPECIFIER} at, which the loader imports (D23).
 * Throws `runtime-missing` when the entry is absent. Pure.
 */
export function runtimeUrl(map) {
    const url = Object.hasOwn(map.imports, SURFACES_RUNTIME_SPECIFIER) ? map.imports[SURFACES_RUNTIME_SPECIFIER] : undefined;
    if (url === undefined)
        throw new ImportMapError('runtime-missing', `the import map has no '${SURFACES_RUNTIME_SPECIFIER}' entry`);
    return url;
}
//# sourceMappingURL=index.js.map